/**
 * Shared utilities for extracting plan data from messages
 */

import { callArtifact } from '@/helpers/artifact.helpers';
import type {
  MessageContent,
  ThreadMessage,
  ToolCallContent,
} from '@/types/messages';
import type { TodoItemData } from '@/types';
import type { Plan } from '@/types/artifacts.types';

/** The plan a part's call draws; null for any other part. */
function planOf(part: MessageContent): Plan | null {
  if (part.type !== 'tool-call') return null;
  const artifact = callArtifact(part as ToolCallContent);
  return artifact?.kind === 'plan' ? artifact : null;
}

/**
 * Extracted plan data from tool calls
 */
export interface ExtractedPlanData {
  todos: TodoItemData[];
  planText: string | null;
  planId: string | null;
  messageId?: string;
}

/**
 * Extract plan data from thread messages.
 * Processes messages in reverse to find the latest plan tool call.
 *
 * @param messages - Array of thread messages to search
 * @returns Extracted plan data or null if not found
 */
export function extractPlanDataFromMessages(
  messages: ThreadMessage[]
): ExtractedPlanData | null {
  // Process messages in reverse to find the latest plan tool call
  const reversedMessages = [...messages].reverse();

  for (const message of reversedMessages) {
    if (message.role !== 'assistant' || !message.content) {
      continue;
    }

    // Process content in reverse to get the latest tool call in the message
    const contentArray = Array.isArray(message.content)
      ? message.content
      : [];
    const reversedContent = [...contentArray].reverse();

    for (const part of reversedContent) {
      const plan = planOf(part);
      if (!plan) {
        continue;
      }

      const { plan_id: planId, plan_text: planText } = plan;

      // Todos from a call that returned only
      const todos: TodoItemData[] =
        plan.ok === null ? [] : [...(plan.todos ?? [])];

      // Return extracted data if we have planText or todos
      if (planText || todos.length > 0) {
        return {
          todos,
          planText,
          planId,
          messageId: message.id,
        };
      }
    }
  }

  return null;
}

/**
 * Check if there's a plan tool call with planText in messages.
 *
 * @param messages - Array of thread messages to search
 * @returns True if a plan with planText is found
 */
export function hasPlanInMessages(
  messages: ThreadMessage[]
): boolean {
  for (const message of messages) {
    if (message.role !== 'assistant' || !message.content) {
      continue;
    }

    const contentArray = Array.isArray(message.content)
      ? message.content
      : [];

    for (const part of contentArray) {
      if (planOf(part)?.plan_text?.trim()) {
        return true;
      }
    }
  }

  return false;
}

